import React, { Suspense, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";

// Mounts a code-split section only after it scrolls near the viewport.
// Reserves vertical space (minHeight) before AND during chunk download to
// prevent CLS / scroll-jump. Idle fallback ensures non-scrollers (and most
// modern crawlers) still get the content shortly after page load.
function LazySection({
  Component,
  minHeight = 400,
  rootMargin = "500px 0px",
  idleTimeoutMs = 1500,
}) {
  const ref = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return undefined;
    const el = ref.current;

    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setShouldRender(true);
      return undefined;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldRender(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    if (el) obs.observe(el);

    // Deterministic idle fallback so users who never scroll, and most modern
    // crawlers that run JS, still get the section content within a fixed
    // window. Not relied on for SEO of crawl-critical text — that stays
    // eager / static.
    const ric =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback
        : (cb) => window.setTimeout(cb, idleTimeoutMs);
    const cic =
      typeof window.cancelIdleCallback === "function"
        ? window.cancelIdleCallback
        : window.clearTimeout;

    const handle = ric(() => setShouldRender(true), { timeout: idleTimeoutMs });

    return () => {
      obs.disconnect();
      try {
        cic(handle);
      } catch (e) {
        // ignore — handle type may not match in older runtimes
      }
    };
  }, [shouldRender, rootMargin, idleTimeoutMs]);

  return (
    <Box ref={ref} sx={{ minHeight: shouldRender ? undefined : minHeight }}>
      {shouldRender && (
        <Suspense fallback={<Box sx={{ minHeight }} aria-hidden="true" />}>
          <Component />
        </Suspense>
      )}
    </Box>
  );
}

LazySection.propTypes = {
  Component: PropTypes.elementType.isRequired,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  rootMargin: PropTypes.string,
  idleTimeoutMs: PropTypes.number,
};

export default LazySection;
